import { useQuery, useQueries, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import {
    getProductList,
    getOrganizationPurchasedProducts,
    getProductDetail,
    getProductDetailForClassForm,
    getProductPriceList,
    type GetProductPriceListQueryParams,
    createProduct,
    updateProduct,
    deleteProduct,
    createProductPrice,
    deleteProductPrice,
} from '@/services/productService';
import type {
    GetProductListResponseDto,
    GetPurchasedProductListResponseDto,
    GetProductDetailResponseDto,
    GetProductPriceListResponseDto,
    CreateProductRequestDto,
    UpdateProductRequestDto,
    CreateProductPriceRequestDto,
    ProductListItemDto,
    ProductPriceCurrency,
    ProductPriceListItemDto,
    ProductType,
    PurchasedProductItemDto,
} from '@/types/api/product';
import { formatProductPriceValidityLabel } from '@/lib/productPriceValidityLabel';

export const productKeys = {
    all: ['product'] as const,
    lists: () => [...productKeys.all, 'list'] as const,
    list: (params: object) => [...productKeys.lists(), params] as const,
    organizationPurchased: (organizationId: number) =>
        [...productKeys.all, 'organizationPurchased', organizationId] as const,
    details: () => [...productKeys.all, 'detail'] as const,
    detail: (id: number) => [...productKeys.details(), id] as const,
    /** 클래스 폼: /items + (기본) price/list 또는 (기관) purchased 행 캐시 */
    classFormDetails: () => [...productKeys.all, 'classFormDetail'] as const,
    classFormDetail: (productId: number, titleTag: string, typeTag: string, purchasedKey: string) =>
        [...productKeys.classFormDetails(), productId, titleTag, typeTag, purchasedKey] as const,
    priceList: () => [...productKeys.all, 'priceList'] as const,
    /** 상품 관리(판매단위 그리드): language 기준 전 페이지 price/list */
    allPriceListRows: (language: string) => [...productKeys.priceList(), 'allRows', language] as const,
    /** GET /product/list 전 페이지 — 기기·클래스 UI에서 productId → type 보강 */
    allListRows: (language: string) => [...productKeys.lists(), 'allListRows', language] as const,
};

const ALL_PRICE_LIST_PAGE = 100;
const ALL_PRICE_LIST_MAX_PAGES = 200;
const ALL_PRODUCT_LIST_PAGE = 200;
const ALL_PRODUCT_LIST_MAX_PAGES = 200;

/**
 * `GET /product/list` 전 페이지 수집 — 기기 단위·클래스 UI에서 `productId`로 `type` 보강(첫 페이지 500자 한도 이슈 방지)
 */
export const useAllProductListRows = (options?: { enabled?: boolean }) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: productKeys.allListRows(language),
        queryFn: async (): Promise<{
            productList: ProductListItemDto[];
            totalCount: number;
        }> => {
            const merged: ProductListItemDto[] = [];
            let page = 1;
            let totalCount = 0;
            for (;;) {
                const chunk = await getProductList({
                    page,
                    pageSize: ALL_PRODUCT_LIST_PAGE,
                    language,
                });
                if (page === 1) totalCount = chunk.totalCount ?? 0;
                merged.push(...chunk.productList);
                if (chunk.productList.length < ALL_PRODUCT_LIST_PAGE) break;
                if (totalCount > 0 && merged.length >= totalCount) break;
                if (page >= ALL_PRODUCT_LIST_MAX_PAGES) break;
                page += 1;
            }
            return { productList: merged, totalCount: totalCount || merged.length };
        },
        enabled: options?.enabled !== false,
        staleTime: 60 * 1000,
    });
};

/**
 * `GET /product/price/list` — OpenAPI 쿼리(page, pageSize, productType, isActive, searchText) + language
 * `/admin/product-management` 등 **서버 페이지** 목록
 */
export const useProductPriceList = (
    args: GetProductPriceListQueryParams,
    options?: Omit<UseQueryOptions<GetProductPriceListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    const { page = 1, pageSize = 20, searchText, productType, isActive } = args;
    const st = searchText?.trim() ?? '';
    return useQuery({
        queryKey: [
            ...productKeys.priceList(),
            'paged',
            language,
            page,
            pageSize,
            st,
            productType ?? '',
            isActive === undefined ? 'all' : isActive,
        ] as const,
        queryFn: ({ signal }) =>
            getProductPriceList(
                { page, pageSize, searchText: st || undefined, productType, isActive },
                { language, signal }
            ),
        staleTime: 0,
        refetchOnWindowFocus: true,
        ...options,
    });
};

/**
 * `GET /product/price/list` 를 전 페이지 수집 — (레거시) 내부·다른 경로가 전체 행이 필요할 때
 */
export const useAllProductPriceList = (options?: { enabled?: boolean }) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: productKeys.allPriceListRows(language),
        queryFn: async (): Promise<{
            rows: ProductPriceListItemDto[];
            totalRowCount: number;
        }> => {
            const first = await getProductPriceList(
                { page: 1, pageSize: ALL_PRICE_LIST_PAGE },
                { language }
            );
            const rows = [...(first.list ?? [])];
            const totalPages = first.totalPages ?? 1;
            for (
                let page = 2;
                page <= totalPages && page <= ALL_PRICE_LIST_MAX_PAGES;
                page++
            ) {
                const next = await getProductPriceList(
                    { page, pageSize: ALL_PRICE_LIST_PAGE },
                    { language }
                );
                rows.push(...(next.list ?? []));
            }
            return { rows, totalRowCount: rows.length };
        },
        enabled: options?.enabled !== false,
        staleTime: 0,
        refetchOnWindowFocus: true,
    });
};

export const useProductList = (
    params: { page: number; pageSize: number },
    options?: Omit<UseQueryOptions<GetProductListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: productKeys.list({ ...params, language }),
        queryFn: () => getProductList({ ...params, language }),
        staleTime: 0,
        refetchOnMount: true,
        ...options,
    });
};

/** GET /product/organization/purchased */
export const useOrganizationPurchasedProducts = (
    organizationId: number,
    options?: Omit<UseQueryOptions<GetPurchasedProductListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const oid = Number.isFinite(organizationId) && organizationId > 0 ? organizationId : 0;
    const language = useApiLanguage();
    return useQuery({
        queryKey: [...productKeys.organizationPurchased(oid), language],
        queryFn: () => getOrganizationPurchasedProducts(oid, language),
        enabled: oid > 0,
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useProductDetail = (
    productId: number,
    options?: Omit<UseQueryOptions<GetProductDetailResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: [...productKeys.detail(productId), language],
        queryFn: () => getProductDetail(productId, { language }),
        enabled: productId > 0,
        staleTime: 2 * 60 * 1000,
        ...options,
    });
};

export type UseClassFormProductDetailOptions = Omit<
    UseQueryOptions<GetProductDetailResponseDto>,
    'queryKey' | 'queryFn'
> & {
    /** 목록·구매행에서 온 제목/타입(판매단위 목록에 없을 때 보조) */
    titleFallback?: string;
    typeFallback?: ProductType;
    /** 기관 구매 행: GET /product/price/list 대신 이 행의 productPriceId 등으로 판매단위 조합 */
    purchasedPriceRow?: PurchasedProductItemDto;
};

/** 클래스 생성·수정: 상세 GET 대신 /items + 판매단위 목록(또는 기관 구매 행) 조합 */
export const useClassFormProductDetail = (productId: number, options?: UseClassFormProductDetailOptions) => {
    const { titleFallback = '', typeFallback, purchasedPriceRow, ...queryOptions } = options ?? {};
    const language = useApiLanguage();
    const typeTag = typeFallback ?? 'CONTENT';
    const purchasedKey =
        purchasedPriceRow != null
            ? `org:${purchasedPriceRow.orderId}-${purchasedPriceRow.productId}-${purchasedPriceRow.productPriceId}`
            : 'catalog';
    return useQuery({
        queryKey: [...productKeys.classFormDetail(productId, titleFallback, typeTag, purchasedKey), language],
        queryFn: () =>
            getProductDetailForClassForm(
                productId,
                {
                    title: titleFallback,
                    type: typeTag,
                    purchasedPriceRow,
                },
                language
            ),
        enabled: productId > 0,
        staleTime: 2 * 60 * 1000,
        ...queryOptions,
    });
};

/**
 * productPriceId 배열을 받아서 { [productPriceId]: currency } 맵을 반환합니다.
 * 백엔드 주문 API가 currency를 내려주지 않을 때 보완용으로 사용합니다.
 */
export const useProductPriceCurrencyMap = (
    productPriceIds: number[]
): Record<number, ProductPriceCurrency> => {
    const enabled = productPriceIds.length > 0;
    const language = useApiLanguage();
    const { data } = useQuery({
        queryKey: [...productKeys.priceList(), language],
        queryFn: async (): Promise<GetProductPriceListResponseDto> => {
            const first = await getProductPriceList({ page: 1 }, { language });
            const merged = [...(first.list ?? [])];
            const totalPages = first.totalPages ?? 1;
            for (let page = 2; page <= totalPages; page++) {
                const next = await getProductPriceList({ page }, { language });
                merged.push(...(next.list ?? []));
            }
            return {
                ...first,
                list: merged,
                totalCount: merged.length,
                page: 1,
                totalPages: 1,
            };
        },
        enabled,
        staleTime: 5 * 60 * 1000,
    });

    if (!data?.list) return {};
    const map: Record<number, ProductPriceCurrency> = {};
    for (const item of data.list) {
        if (productPriceIds.includes(item.productPriceId)) {
            map[item.productPriceId] = item.currency;
        }
    }
    return map;
};

/**
 * productPriceId 배열 → { [productPriceId]: costPrice } 맵
 *
 * 전략:
 * 1. /product/price/list 로 productPriceId → productId 매핑
 * 2. 해당 productId 들에 대해 /product/{productId} 상세 조회
 * 3. 상세의 prices[].costPrice 에서 productPriceId 별 원가 추출
 *
 * /product/price/list 는 costPrice 를 반환하지 않으므로
 * 반드시 프로덕트 상세를 통해 가져와야 한다.
 */
export const useProductPriceUnitCostMap = (
    productPriceIds: number[]
): Record<number, number> => {
    const enabled = productPriceIds.length > 0;
    const language = useApiLanguage();

    // Step 1: price/list 로 productPriceId → productId 매핑
    const { data: priceListData } = useQuery({
        queryKey: [...productKeys.priceList(), 'unitCost', language],
        queryFn: async (): Promise<GetProductPriceListResponseDto> => {
            const first = await getProductPriceList({ page: 1 }, { language });
            const merged = [...(first.list ?? [])];
            const totalPages = first.totalPages ?? 1;
            for (let page = 2; page <= totalPages; page++) {
                const next = await getProductPriceList({ page }, { language });
                merged.push(...(next.list ?? []));
            }
            return { ...first, list: merged, totalCount: merged.length, page: 1, totalPages: 1 };
        },
        enabled,
        staleTime: 5 * 60 * 1000,
    });

    // productPriceId → productId 맵
    const priceIdToProductId: Record<number, number> = {};
    if (priceListData?.list) {
        for (const item of priceListData.list) {
            if (productPriceIds.includes(item.productPriceId) && item.productId) {
                priceIdToProductId[item.productPriceId] = item.productId;
            }
        }
    }
    const uniqueProductIds = [...new Set(Object.values(priceIdToProductId))];

    // Step 2: 각 productId 에 대해 상세 조회
    const detailQueries = useQueries({
        queries: uniqueProductIds.map((productId) => ({
            queryKey: [...productKeys.detail(productId), language],
            queryFn: () => getProductDetail(productId, { language }),
            enabled: uniqueProductIds.length > 0,
            staleTime: 5 * 60 * 1000,
        })),
    });

    // Step 3: prices[].costPrice 에서 productPriceId 별 원가 추출
    const map: Record<number, number> = {};
    for (const result of detailQueries) {
        if (!result.data?.prices) continue;
        for (const price of result.data.prices) {
            if (!productPriceIds.includes(price.productPriceId)) continue;
            const cp = price.costPrice;
            if (typeof cp === 'number' && Number.isFinite(cp) && cp >= 0) {
                map[price.productPriceId] = cp;
            }
        }
    }
    return map;
};

/**
 * 주문 상세 등: 품목에 사용기간이 없을 때 productPriceId → GET /product/{id} 의 prices[] 로 표시문 보강.
 * useProductPriceUnitCostMap 과 동일한 React Query 키(가격 목록·프로덕트 상세)로 네트워크 요청을 공유합니다.
 */
export const useProductPriceValidityLabelMap = (productPriceIds: number[]): Record<number, string> => {
    const enabled = productPriceIds.length > 0;
    const language = useApiLanguage();
    const { data: priceListData } = useQuery({
        queryKey: [...productKeys.priceList(), 'unitCost', language],
        queryFn: async (): Promise<GetProductPriceListResponseDto> => {
            const first = await getProductPriceList({ page: 1 }, { language });
            const merged = [...(first.list ?? [])];
            const totalPages = first.totalPages ?? 1;
            for (let page = 2; page <= totalPages; page++) {
                const next = await getProductPriceList({ page }, { language });
                merged.push(...(next.list ?? []));
            }
            return { ...first, list: merged, totalCount: merged.length, page: 1, totalPages: 1 };
        },
        enabled,
        staleTime: 5 * 60 * 1000,
    });

    const priceIdToProductId: Record<number, number> = {};
    if (priceListData?.list) {
        for (const item of priceListData.list) {
            if (productPriceIds.includes(item.productPriceId) && item.productId) {
                priceIdToProductId[item.productPriceId] = item.productId;
            }
        }
    }
    const uniqueProductIds = [...new Set(Object.values(priceIdToProductId))];

    const detailQueries = useQueries({
        queries: uniqueProductIds.map((productId) => ({
            queryKey: [...productKeys.detail(productId), language],
            queryFn: () => getProductDetail(productId, { language }),
            enabled: uniqueProductIds.length > 0,
            staleTime: 5 * 60 * 1000,
        })),
    });

    const map: Record<number, string> = {};
    for (const result of detailQueries) {
        if (!result.data?.prices) continue;
        for (const price of result.data.prices) {
            if (!productPriceIds.includes(price.productPriceId)) continue;
            map[price.productPriceId] = formatProductPriceValidityLabel(
                price.validityPeriod,
                price.validityPeriodUnit
            );
        }
    }
    return map;
};

export const useCreateProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateProductRequestDto) => createProduct(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
            queryClient.invalidateQueries({ queryKey: productKeys.priceList() });
        },
    });
};

export const useUpdateProduct = (productIdArg?: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args: UpdateProductRequestDto | { productId: number; data: UpdateProductRequestDto }) => {
            if ('productId' in args && 'data' in args) {
                return updateProduct(args.productId, args.data);
            }
            if (!productIdArg) throw new Error('productId가 필요합니다.');
            return updateProduct(productIdArg, args as UpdateProductRequestDto);
        },
        onSuccess: (_data, args) => {
            const pid = 'productId' in args ? args.productId : (productIdArg ?? 0);
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
            queryClient.invalidateQueries({ queryKey: productKeys.priceList() });
            if (pid > 0) {
                queryClient.invalidateQueries({ queryKey: productKeys.detail(pid) });
                queryClient.invalidateQueries({ queryKey: productKeys.classFormDetails() });
            }
        },
    });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (productId: number) => deleteProduct(productId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
            queryClient.invalidateQueries({ queryKey: productKeys.priceList() });
            queryClient.invalidateQueries({ queryKey: productKeys.classFormDetails() });
        },
    });
};

export const useCreateProductPrice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateProductPriceRequestDto) => createProductPrice(data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.productId) });
            queryClient.invalidateQueries({ queryKey: productKeys.classFormDetails() });
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
            queryClient.invalidateQueries({ queryKey: productKeys.priceList() });
        },
    });
};

export const useDeleteProductPrice = (productId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (productPriceId: number) => deleteProductPrice(productPriceId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
            queryClient.invalidateQueries({ queryKey: productKeys.classFormDetails() });
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
            queryClient.invalidateQueries({ queryKey: productKeys.priceList() });
        },
    });
};
